import React, { useContext, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { AppContext } from '@state/context'
import { openPanelWithData } from '@state/actions/panelsActions'
import {
  PANELS,
  BUTTON_SIZE,
  BUTTON_TYPE,
  CONTAINER_STYLE,
  HEADING_TYPE,
  TEXT_SIZES,
  DIVIDER_SIZE,
  COLORS,
  SCHOOL_RESOURCE_TYPES,
} from '@helpers/constants'
import SubHeader from '@views/SubHeader'
import Container from '@components/Container'
import Heading from '@components/Heading'
import Divider from '@components/Divider'
import Text from '@components/Text'
import Loader from '@components/Loader'
import Button from '@components/Button'
import SelectButton from '@components/SelectButton'
import GridColumns from '@components/GridColumns'
import StudentList from '@components/StudentList'
import SharedBooksList from '@components/SharedBooksList'
import { useUpsertSchoolGroup } from '@hooks/mutations/useUpsertSchoolGroup'
import { useSchoolGroupsOptions } from '@hooks/useSchoolGroupsOptions'
import { useSchoolGroup } from '@hooks/useSchoolGroup'

const SingleSchoolGroup = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [, dispatch] = useContext(AppContext)
  const { schoolGroup, error } = useSchoolGroup(id)
  const { schoolGroupsOptions } = useSchoolGroupsOptions()
  const breadcrumb = useMemo(() => (schoolGroup && {
    label: schoolGroup?.name,
  }), [schoolGroup])

  return (
    <>
      <SubHeader breadcrumb={breadcrumb} >
        <SubHeader.MiddleSection />

        <SubHeader.RightSection>
          {schoolGroup && schoolGroupsOptions?.length > 0 && (
            <SelectButton
              onChange={(e) => {
                navigate(e.target.value, { state: { replaceLastBreadcrumb: true }})
              }}
              name={'schoolGroup'}
              id={'schoolGroup'}
              options={schoolGroupsOptions}
              defaultValue={`/grupp/${schoolGroup.id}`}
              extraClasses={'max-w-[200px]'}
            />
          )}
        </SubHeader.RightSection>
      </SubHeader>

      {!schoolGroup && !error && <Loader />}
      {error && <Text>Någonting gick fel när vi skulle hämta grupp</Text>}

      {schoolGroup && !error && (
        <div
          className={'flex flex-col'}
          style={{ minHeight: 'calc(100vh - 146px)' }}
        >
          <Container size={CONTAINER_STYLE.DESKTOP}>
            <div className={'flex justify-center'}>
              <Heading headingLevel={HEADING_TYPE.H1} size={TEXT_SIZES.XXLARGE}>
                {schoolGroup?.name}
              </Heading>
            </div>

            <Divider size={DIVIDER_SIZE.SMALL} />

            <div className={'flex items-center justify-between'}>
              <Button
                type={BUTTON_TYPE.SECONDARY}
                size={BUTTON_SIZE.MEDIUM}
                icon={'PlusCircle'}
                iconLeft
                margin
                handleClick={() => {
                  dispatch(
                    openPanelWithData({
                      panel: PANELS.ADD_STUDENT_TO_SCHOOL_GROUP,
                      data: {
                        schoolId: schoolGroup?.school?.id,
                        schoolGroupId: schoolGroup?.id,
                      },
                    })
                  )
                }}
              >
                Lägg till elever
              </Button>
              <Button
                type={BUTTON_TYPE.SECONDARY}
                size={BUTTON_SIZE.MEDIUM}
                // textColor={COLORS.WHITE.TEXT}
                icon={'Pencil'}
                iconLeft
                margin
                handleClick={() => {
                  dispatch(
                    openPanelWithData({
                      panel: PANELS.EDIT_SCHOOL_GROUP,
                      data: { schoolGroupId: schoolGroup.id },
                    })
                  )
                }}
              >
                Redigera
              </Button>
            </div>
          </Container>

          <Divider line size={DIVIDER_SIZE.SMALL} />

          <div className={'xl:container xl:mx-auto px-10 flex-auto h-[1px]'}>
            <GridColumns cols={4} classes={'h-full gap-7'}>
              <div className={'h-full border-r border-mediumGrey'}>
                <Divider size={DIVIDER_SIZE.BASE} />
                <StudentList
                  items={schoolGroup.students}
                  resourceId={schoolGroup.id}
                  updateHook={useUpsertSchoolGroup}
                />
                <Divider size={DIVIDER_SIZE.BASE} />
              </div>
              <div className={'col-span-3'}>
                <Divider size={DIVIDER_SIZE.BASE} />

                <SharedBooksList
                  items={schoolGroup.books}
                  resourceType={SCHOOL_RESOURCE_TYPES.GROUP}
                  resource={schoolGroup}
                />

                <Divider size={DIVIDER_SIZE.BASE} />
              </div>
            </GridColumns>
          </div>
        </div>
      )}
    </>
  )
}

export default SingleSchoolGroup
